import * as stylex from '@stylexjs/stylex';
import { colors } from '@seeqdev/qomponents/colors.stylex';

/** Shared layouts for model training forms and model property panels. */
export const modelStyles = stylex.create({
  trainingWindow: { padding: 10, marginTop: 0, marginBottom: 8 },
  modelPicker: { width: 180 },
  advancedSection: { marginTop: 15 },
  conditionField: { marginTop: 8 },
  parameterSection: { marginTop: 15, marginBottom: 10 },
  parameterField: { marginTop: 5 },
  numericInput: { width: 100 },
  outputToggle: { marginRight: 5 },
  loadingIndicator: { marginLeft: 5 },
  outputSection: { marginTop: 10 },
  dependentOption: { marginLeft: 20 },
  compactField: { marginBottom: 0 },
  chartSelector: { marginBottom: 10 },
  chartContent: { padding: 13 },
  outputLabel: { marginLeft: 10 },
  modelSection: { marginBottom: 20 },
  initialSection: { marginTop: 0 },
  compactParameter: { marginTop: 5, marginBottom: 0 },
  outputCount: { width: 100, marginBottom: 2 },
  outputList: { display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: 250 },
  modelActions: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  formColumn: { display: 'flex', flexDirection: 'column' },
  alignedParameters: { alignItems: 'center' },
  outputHint: { fontStyle: 'italic', paddingRight: 10, paddingLeft: 10, marginBottom: 15 },
  selectionHint: { fontStyle: 'italic', paddingLeft: 5, marginBottom: 15 },
  modelHint: { fontStyle: 'italic', marginBottom: 15, marginLeft: 10, marginRight: 10 },
  predictionPeriod: { width: 75, marginLeft: 3 },
  predictionPeriodCompact: { width: 75, marginLeft: 3, marginBottom: 0 },
  predictionValue: { width: 100, marginLeft: 3, marginBottom: 0 },
  predictionOption: { marginLeft: 35 },
  executionSection: { marginTop: '1.25rem' },
  propertyHeading: { display: 'flex', flexDirection: 'row', marginTop: '0.625rem', justifyContent: 'space-between' },
  propertyRow: { display: 'flex', flexDirection: 'row', marginTop: '0.625rem' },
  staleModel: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '0.625rem',
    height: 250,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingStatus: { marginRight: '0.3125rem' },
  modelLegend: { marginTop: '0.625rem' },
  copyAction: { display: 'table-cell', marginRight: 3 },
  interactive: { cursor: 'pointer' },
  modelChart: { marginTop: '0.3125rem', minHeight: 400 },
  chartDescription: { paddingLeft: '0.625rem', paddingRight: '0.625rem' },
  chartIntroduction: { marginTop: '0.625rem', marginBottom: '0.3125rem' },
  clusterCenters: { overflow: 'hidden', overflowX: 'auto' },
  chartToolbar: { display: 'flex', flexDirection: 'row', marginTop: '0.625rem', marginRight: '0.625rem' },
  predictionLabel: { display: 'inline-flex', alignItems: 'center' },
  polynomialDegreeSelect: { marginLeft: '0.3125rem', backgroundColor: colors.fieldBackground },
  tableSection: { display: 'flex', flexDirection: 'column', marginBottom: 0, overflowY: 'auto' },
  tableViewport: { overflow: 'auto', flex: '1 1 auto' },
  inputNameHeading: { minWidth: 100 },
  inputSignalHeading: { maxWidth: 370 },
  trailingHeading: { width: '1%' },
  emptyModel: { textAlign: 'center', fontStyle: 'italic' },
  emptyModelContent: { flex: '1 1 auto', overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
  inputName: { verticalAlign: 'middle', display: 'flex', flexDirection: 'row' },
  modelPanel: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 6,
    paddingLeft: 10,
    paddingRight: 10,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.toolPanelBorder,
    marginBottom: '0.9375rem',
  },
  panelHeading: {
    color: colors.legacyToolsPrimary,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: '0.3125rem',
    paddingBottom: '0.3125rem',
  },
  panelTitle: { marginLeft: '0.625rem' },
  disabled: { color: colors.modelPropertyDisabled },
  divider: {
    marginLeft: -4,
    marginRight: -4,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: colors.toolPanelBorder,
  },
  refreshAction: {
    marginLeft: '0.3125rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    pointerEvents: 'auto',
    color: colors.legacyToolsLink,
  },
  parameterLabel: { fontWeight: 'normal', textDecoration: 'none', color: colors.legacyToolsPrimary },
  loadingsSection: { width: '98%', marginTop: '0.3125rem', minHeight: 400 },
  outputTable: { width: '100%', height: '100%', overflow: 'hidden', marginBottom: '0.1875rem' },
  signalSelector: { minWidth: 200 },
  flexibleSelector: { flex: '1 1 auto', overflow: 'hidden' },
  modelDivider: { paddingBottom: 5, borderBottomWidth: 1, borderBottomStyle: 'solid' },
  propertyTable: {
    paddingBottom: 65,
    width: '99%',
    marginBottom: '1rem',
    color: colors.bodyText,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.modelPropertyBorder,
    borderCollapse: 'collapse',
  },
  fixedPropertyTable: { tableLayout: 'fixed' },
  propertyCell: {
    wordBreak: 'break-all',
    padding: '2px 7px',
    verticalAlign: 'top',
    borderWidth: 0,
    borderRightWidth: { 'default': 0, ':not(:last-child)': 1 },
    borderRightStyle: 'solid',
    borderRightColor: colors.modelPropertyBorder,
  },
  propertyHeader: {
    wordBreak: 'break-all',
    padding: '2px 7px',
    verticalAlign: 'top',
    color: colors.modelPropertyHeading,
    borderWidth: 0,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: colors.toolTableHeaderBorder,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.toolTableHeaderBorder,
    borderRightWidth: { 'default': 0, ':not(:last-child)': 1 },
    borderRightStyle: 'solid',
    borderRightColor: colors.modelPropertyBorder,
  },
  loadingsViewport: { overflow: 'hidden', overflowX: 'auto' },
  loadingsHeader: { width: 60 },
  propertyResizer: { right: -13 },
  selectionTable: {
    width: '100%',
    marginBottom: '0.1875rem',
    color: colors.bodyText,
    position: 'relative',
    borderCollapse: 'collapse',
    overflowX: 'hidden',
  },
  selectionCell: { position: 'relative', borderWidth: 0, padding: '2px 3px', verticalAlign: 'top' },
  selectionHeader: {
    backgroundColor: colors.background,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderWidth: 0,
    padding: '2px 3px',
    verticalAlign: 'top',
  },
  alternatingInput: { backgroundColor: { 'default': 'transparent', ':nth-of-type(odd)': colors.tableRowAlternate } },
  outputWarning: { paddingRight: 5 },
  outputIcon: { paddingRight: '0.3125rem' },
  displayAction: { paddingTop: 0, paddingLeft: 0 },
  columnChartIcon: { transform: 'rotate(270deg)' },
  propertyTableRow: { backgroundColor: { 'default': 'transparent', ':hover': colors.toolTableRowHover } },
  stripedPropertyRow: { backgroundColor: { 'default': colors.tableRowAlternate, ':hover': colors.toolTableRowHover } },
});
